import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import MainLayoutWithoutFooter from '../components/MainLayoutWithoutFooter';
import Timesheet from '../components/Timesheet';
import { callAPI } from '../utils/apiUtils';
import { apiUrls } from '../utils/apiUrls';
import '../styles/ValidatePinModal.css';

const PIN_LENGTH = 4;

/**
 * Rebuilt on the new Staff/Roles/Permissions module's PIN system (TimesheetController::
 * actionValidatePin, hashed pin_hash via RestaurantEmployee::findByPin) instead of the old
 * "pick your name from a list, then enter your PIN" flow (CustomerController::actionValidatepin,
 * plaintext pin comparison) - PIN alone now identifies the staff member, so there's one fewer
 * step, and it's the same PIN every other staff-gated action in this app already uses.
 *
 * Also still the redirect target CheckoutItem.js falls back to for a dine-in delete on a
 * restaurant that hasn't turned on staff PIN login (?tbtl=<table_id>&itid=<cart_id> in the URL).
 * The legacy version of that fallback round-tripped a client-supplied employee id through the
 * URL (?validated=1&resDetail=<id>_<x>_<emp_id>) and trusted it at face value. This modernizes
 * that fallback in place, same redirect-here / enter-PIN / land-back-on-payment-view shape, but
 * the PIN is validated the same secure way every other staff-gated action in this app already
 * uses (StaffAuthController::actionValidatePin - hashed PIN -> employee -> server-recomputed
 * permissions), and the delete happens right here with that server-verified employee id, never a
 * client-supplied one. One accepted gap: this path has no order id to attach (only table/item
 * id survive the redirect), so a delete authorised this way won't show up in the Revenue page's
 * "Deleted Items & Orders by Staff" report the way the inline flow's deletes do - the item itself
 * is still correctly deleted and attributed to the authorising employee.
 */
const ClockinClockout = () => {
  const [digits, setDigits] = useState(Array(PIN_LENGTH).fill(''));
  const [employee, setEmployee] = useState(null);
  const [validating, setValidating] = useState(false);
  const inputRefs = useRef([]);

  const params = new URLSearchParams(window.location.search);
  const tableId = params.get('tbtl');
  const itemId = params.get('itid');
  const isItemDeleteAuth = Boolean(tableId && itemId);

  const resetPinEntry = () => {
    setDigits(Array(PIN_LENGTH).fill(''));
    setTimeout(() => inputRefs.current[0]?.focus(), 50);
  };

  const authoriseItemDelete = async (pin) => {
    setValidating(true);

    let permissions;
    let employeeId;
    try {
      const res = await callAPI(apiUrls.STAFF_AUTH_VALIDATE_PIN, {}, 'POST', { pin });
      permissions = res.data?.data?.permissions || [];
      employeeId = res.data?.data?.employee_id ?? null;
    } catch (err) {
      toast.error(err.response?.data?.errors?.pin?.[0] || 'Invalid PIN');
      resetPinEntry();
      setValidating(false);
      return;
    }

    if (!permissions.includes('order.delete_item')) {
      toast.error('This PIN is not authorised to delete items.');
      resetPinEntry();
      setValidating(false);
      return;
    }

    try {
      await callAPI(apiUrls.REMOVE_CART_ITEM, {}, 'POST', {
        id: itemId,
        from: 'payment-page',
        orderType: 3,
        del_emp_id: employeeId,
      });
      toast.success('Item deleted');
      window.location.href = `/payment-view?table_id=${tableId}`;
    } catch (err) {
      toast.error('Failed to delete the item. Please try again.');
      setValidating(false);
    }
  };

  const submitPin = async (pin) => {
    if (isItemDeleteAuth) {
      await authoriseItemDelete(pin);
      return;
    }

    setValidating(true);
    try {
      const res = await callAPI(apiUrls.TIMESHEET_VALIDATE_PIN, {}, 'POST', { pin });
      if (res.data?.data) {
        setEmployee(res.data.data);
      } else {
        toast.error('Invalid PIN');
        resetPinEntry();
      }
    } catch (err) {
      toast.error(err.response?.data?.errors?.pin?.[0] || 'Invalid PIN');
      resetPinEntry();
    } finally {
      setValidating(false);
    }
  };

  const handleChange = (index, rawValue) => {
    const value = rawValue.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = value;
    setDigits(next);

    if (value && index < PIN_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
    if (value && index === PIN_LENGTH - 1) {
      submitPin(next.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') {
      submitPin(digits.join(''));
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, PIN_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(PIN_LENGTH).fill('');
    pasted.split('').forEach((char, i) => { next[i] = char; });
    setDigits(next);
    if (pasted.length === PIN_LENGTH) {
      submitPin(pasted);
    } else {
      inputRefs.current[pasted.length]?.focus();
    }
  };

  if (employee) {
    return (
      <Timesheet
        employee={employee}
        onSwitchUser={() => {
          setEmployee(null);
          resetPinEntry();
        }}
      />
    );
  }

  return (
    <MainLayoutWithoutFooter>
      <div className="pageContent timesheetsPage pb-3">
        <div className="container d-flex justify-content-center">
          <div className="pinModalBody" style={{ maxWidth: 380, width: '100%' }}>
            <div className="pinModalIcon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h1 className="pinModalTitle">{isItemDeleteAuth ? 'Authorise Item Deletion' : 'Timesheet'}</h1>
            <p className="pinModalSubtitle">
              {isItemDeleteAuth
                ? 'Manager PIN required to delete this item'
                : 'Enter your 4-digit PIN to check in or out'}
            </p>

            <div className="pinDigitsRow" onPaste={handlePaste}>
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  className="pinDigitBox" data-no-scan="true"
                  value={digit}
                  disabled={validating}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  autoFocus={index === 0}
                />
              ))}
            </div>

            <button
              className="pinValidateBtn"
              onClick={() => submitPin(digits.join(''))}
              disabled={validating}
            >
              {validating ? 'Checking...' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </MainLayoutWithoutFooter>
  );
};

export default ClockinClockout;
